/** POST — regional branch login (public; may return HTTP 200 with success: false). */
export const LOGIN_BRANCH_API = '/LoginBranch';
/** POST — invalidate branch session on server; Bearer JWT (no body). */
export const LOGOUT_BRANCH_API = '/LogoutBranch';
/** POST — public; body `{ email }`; sends forgot-password email when user exists. */
export const BRANCH_FORGOT_PASSWORD = '/BranchForgotPassword';
/** POST — public; body `{ password, retype_password }`; token only in query string. */
export const BRANCH_RESET_PASSWORD = '/BranchResetPassword';

/** @param {string} token reset token (hashed value from email link or API). */
export const buildBranchResetPasswordUrl = (token) => {
  const qs = new URLSearchParams();
  qs.set('token', token == null ? '' : String(token));
  return `${BRANCH_RESET_PASSWORD}?${qs.toString()}`;
};
export const GET_SERVING_ZIPCODE_ADMIN_API = '/GetServingZipcodeForAdmin';
/** GET/POST — map ZIP boundaries; query/body `zipcodes` (comma-separated zips or regional branch name). */
export const GET_MAP_ZIPCODE_BOUNDRIES = '/GetMapZipcodeBoundries';
export const ONLOAD_INFORMATION_API = '/OnLoadKitchenInformation';
/** POST — regional branch session bootstrap (JWT sent via axios default header). Body: branchName, team_user_id. */
export const ON_LOAD_REG_BRANCH_INFORMATION = '/OnLoadRegBranchInformation';
/** GET — team roster for a branch; query branchName (trimmed + lowercased). */
export const GET_TEAMS_BY_BRANCH = '/GetTeamsByBranch';
/** POST — create a new branch team member with role & route access. */
export const TEAM_SIGNUP_BRANCH = '/TeamSignupBranch';
/** PUT — update TeamRoleAccess-RegBranch (branchName + team_user_id). */
export const UPDATE_TEAM_ROLE_ACCESS_REG_BRANCH = '/UpdateTeamRoleAccessRegBranch';

/** @param {string} branchName */
export const buildGetTeamsByBranchUrl = (branchName) => {
  const bn = String(branchName ?? '')
    .trim()
    .toLowerCase();
  const qs = new URLSearchParams();
  qs.set('branchName', bn);
  return `${GET_TEAMS_BY_BRANCH}?${qs.toString()}`;
};
/** GET — zipcode analytics for a regional branch; query managing_branch_name (trimmed + lowercased). */
export const GET_REGIONAL_BRANCH_ZIPCODE_ANALYTICS = '/GetRegionalBranchZipcodeAnalytics';

/** @param {string} managingBranchName */
export const buildGetRegionalBranchZipcodeAnalyticsUrl = (managingBranchName) => {
  const name = String(managingBranchName ?? '')
    .trim()
    .toLowerCase()
    .slice(0, 50);
  const qs = new URLSearchParams();
  qs.set('managing_branch_name', name);
  return `${GET_REGIONAL_BRANCH_ZIPCODE_ANALYTICS}?${qs.toString()}`;
};

/** GET — backend server monitoring flags (regional manager). */
export const GET_SERVER_MONITORING_FLAGS = '/GetServerMonitoringFlags';

/** GET — list Expo push notification tokens. */
export const GET_NOTIFICATION_TOKENS = '/GetNotificationTokens';
/** POST — send Expo push notification to a device (legacy). */
export const SEND_PUSH_NOTIFICATION = '/SendPushNotification';
/** POST — send notification via notification server (recipient + notification body). */
export const SEND_NOTIFICATION = '/SendNotification';

export const GET_KITCHENS_BY_MANAGING_BRANCH = '/GetKitchensByManagingBranch';
/** PUT — regional branch kitchen status / visibility (kitchen_name + managing_branch_name required). */
export const REGIONAL_UPDATE_KITCHEN_OPERATION = '/RegionalUpdateKitchenOperation';
/** POST — active pay-as-you-go orders for a branch/date; body: date, regionalBranch; optional page, limit, timeZone. */
export const GET_RESTAURANT_ORDERS = '/GetRestaurantOrders';
/** POST — next-day promotion store orders; body: date (today MM/DD/YYYY + tomorrow YYYY-MM-DD), regionalBranch, timeZone, page, limit. */
export const GET_NEXT_DAY_PROMO_STORE_ORDERS = '/GetNextDayPromoStoreOrders';
export const GET_RESTAURANT_LEADS_BY_ZIPCODE = '/GetRestaurantLeadByZipcode';
export const UPDATE_RESTAURANT_LEAD_BY_MARKETING = '/UpdateRestaurantLeadByMarketing';
export const UPDATE_RESTAURANT_LEAD_SHOULD_CONTACT_BY_ZIPCODE =
  '/UpdateRestaurantLeadShouldContactByZipcode';
/** POST — fetch ad library creatives and marketing tools for a branch. */
export const GET_AD_LIBRARY_CONTENT = '/GetAdLibraryContent';
/** POST — add a new ad library item for a branch. */
export const ADD_CONTENT_TO_AD_LIBRARY = '/AddContentToAdLibrary';
/** POST — add a marketing tool to the library. */
export const ADD_TOOL_TO_LIBRARY = '/AddToolToLibrary';

export const GET_PRODUCTS_BY_MANAGING_BRANCH = '/GetProductsByManagingBranch';
export const GET_PRODUCT_CATEGORY = '/GetProductCategory';
export const ADD_PRODUCT = '/AddProduct';
/** PUT — regional branch; must not include price, product_promotion, or ingredients. */
export const REGIONAL_UPDATE_PRODUCT = '/RegionalUpdateProduct';
/** PUT — regional branch; status_type, status (boolean), status_notes only (ProductInfo-Master rules). */
export const REGIONAL_UPDATE_PRODUCT_STATUS = '/RegionalUpdateProductStatus';
export const UPLOAD_IMAGE_CLOUDINARY = '/UploadFileCloudinary';
export const OPEN_AI_PRODUCT_NAME = '/AiProductName';
export const OPEN_AI_PRODUCT_DESCRIPTION = '/AiProductDescription';
export const AI_IMAGE_GENERATOR_API = '/AiImageGenerator';
export const REDIS_QUEUE_DRIVER_DELIVERY_ORDERS = '/GetRedisQueuesDriverOrder'; // This end point is in customer_backend_api we dont have in regiobalbranch_backend_api

// -----------------------------
// Menu processing (AI pipeline)
// -----------------------------

export const MENU_START_JOB_PATH = '/start-job';

/**
 * Job list — e.g. GET /jobs/list?kitchen_name=my%20kitchen&status=processing
 */
export const getMenuJobsListPath = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.kitchenName != null && params.kitchenName !== '') {
    qs.set('kitchen_name', String(params.kitchenName));
  }
  if (params.status != null && params.status !== '') {
    qs.set('status', String(params.status));
  }
  const q = qs.toString();
  return q ? `/jobs/list?${q}` : '/jobs/list';
};

export const getMenuStatusPath = (jobId) =>
  `/status/${encodeURIComponent(jobId == null ? '' : String(jobId))}`;

// -----------------------------
// Twilio
// -----------------------------

export const TWILIO_TOKEN_PATH = '/twilio/token';
export const TWILIO_CALL_LOGS_PATH = '/twilio/call-logs';
export const TWILIO_RECORDINGS_PATH = '/twilio/recordings';

// -----------------------------
// Team phone OTP (public route; optional Bearer from interceptor).
// Body: { team_user_id, phone_no (E.164), serviceType, otpNo? }
// -----------------------------

/** POST — `/OtpTeamPhoneVerify` — generateOtp | verifyOtpNo. */
export const OTP_TEAM_PHONE_VERIFY = '/OtpTeamPhoneVerify';
/** PUT — placeholder for “Update phone” from OTP screen until a team endpoint exists. */
export const REG_BRANCH_UPDATE_BRANCH_PHONE = '/RegBranchUpdateBranchPhone';

/** GET /twilio/recordings/:recordingSid */
export const getTwilioRecordingPath = (recordingSid) =>
  `/twilio/recordings/${encodeURIComponent(recordingSid)}`;

/** DELETE /twilio/recordings/:recordingSid */
export const deleteTwilioRecordingPath = (recordingSid) =>
  `/twilio/recordings/${encodeURIComponent(recordingSid)}`;

/** GET|POST /twilio/recordings/:recordingSid/transcription */
export const getTwilioRecordingTranscriptionPath = (recordingSid) =>
  `/twilio/recordings/${encodeURIComponent(recordingSid)}/transcription`;
